import { classNames } from '@framework/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePopupRef } from '@framework/hooks';
import { useCallback } from 'react';
import { faAngleRight, faFileImage } from '@fortawesome/free-solid-svg-icons';
import { MenuButton } from '../MenuButton';
import { DarkTooltip } from '@framework/ui/atoms/Tooltip';
import { useTranslation } from 'react-i18next';
type ButtonContentProps = {
  disabled: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
};

const ButtonContent: React.FC<ButtonContentProps> = ({ disabled, onClick }: ButtonContentProps) => {
  const { t } = useTranslation();
  return (
    <button
      className={classNames(
        'flex w-full items-center pr-4 focus:outline-none',
        disabled ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer hover:bg-gray-200'
      )}
      onClick={onClick}
    >
      <span className="w-10 pl-2">
        <FontAwesomeIcon icon={faFileImage} />
      </span>
      <span className="py-2 pr-2">{t('viewModel.exportImage.button')}</span>
      <FontAwesomeIcon icon={faAngleRight} className="ml-auto" />
    </button>
  );
};

type Props = {
  onExportViewModelAsImage(isWhole: boolean): void;
  disabled: boolean;
};

export const ExportImageButtons: React.FC<Props> = ({ onExportViewModelAsImage, disabled }: Props) => {
  const { t } = useTranslation();
  const { isOpen, popupRef, handleToggle } = usePopupRef<HTMLDivElement>(false);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (disabled) return;
      e.stopPropagation();
      handleToggle();
    },
    [handleToggle, disabled]
  );

  return (
    <div className={classNames('relative', !disabled && 'hover:bg-gray-200')}>
      {disabled ? (
        <DarkTooltip tooltip={t('common.label.licenseRequired')} placement="right">
          <ButtonContent disabled={disabled} onClick={handleClick} />
        </DarkTooltip>
      ) : (
        <ButtonContent disabled={disabled} onClick={handleClick} />
      )}
      <div
        className={classNames(
          'absolute -right-[14rem] top-0 z-50 h-[6rem] w-[14rem] rounded-lg bg-white py-2 shadow-2xl',
          isOpen ? 'block' : 'hidden'
        )}
        ref={popupRef}
      >
        <MenuButton onClick={() => onExportViewModelAsImage(true)} title={t('viewModel.exportImage.entire')} />
        <MenuButton onClick={() => onExportViewModelAsImage(false)} title={t('viewModel.exportImage.visible')} />
      </div>
    </div>
  );
};
